import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

const emptyRule = () => ({
  country: '',
  region: '*',
  tax_code: '*',
  name: '',
  rate_basis_points: 0,
  is_compound: false,
  is_inclusive: false,
  is_active: true,
  note: '',
});

/** Create / edit a per-brand tax rule. */
export default function RuleDialog({ open, onClose, brandId, editing, onSaved }) {
  const [form, setForm] = useState(emptyRule());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...emptyRule(), ...editing } : emptyRule());
  }, [open, editing]);

  const save = () => {
    setSaving(true);
    const call = editing
      ? API.updateTaxRule({ brand_id: brandId, _id: editing._id, patch: form })
      : API.createTaxRule({ brand_id: brandId, patch: form });
    call.then(() => { toast.success('Saved'); onSaved && onSaved(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? 'Edit tax rule' : 'New tax rule'}
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
            <TextField
              size='small'
              required
              label='Country (ISO-2)'
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder='CA'
              sx={{ flex: 1 }}
            />
            <TextField
              size='small'
              label='Region'
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value.toUpperCase().slice(0, 6) || '*' })}
              placeholder='ON or * for any'
              sx={{ flex: 1 }}
            />
            <TextField
              size='small'
              label='Product tax code'
              value={form.tax_code}
              onChange={(e) => setForm({ ...form, tax_code: e.target.value || '*' })}
              placeholder='* for any'
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
            <TextField
              size='small'
              required
              label='Tax name'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder='HST'
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              type='number'
              label='Rate (%)'
              inputProps={{ step: '0.01' }}
              value={Number(form.rate_basis_points || 0) / 100}
              onChange={(e) => setForm({ ...form, rate_basis_points: Math.round(Number(e.target.value) * 100) })}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction='row' spacing='20px' flexWrap='wrap'>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_inclusive}
                  onChange={(e) => setForm({ ...form, is_inclusive: e.target.checked })}
                />
              }
              label='Tax-inclusive (price already includes tax)'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_compound}
                  onChange={(e) => setForm({ ...form, is_compound: e.target.checked })}
                />
              }
              label='Compound (stacked on top of other taxes)'
            />
          </Stack>
          <TextField
            size='small'
            label='Note'
            value={form.note || ''}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label='Active'
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || !form.country || !form.name}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
